import React, { useEffect, useState } from 'react';
import api from '../api/api';
import TopNav from '../components/TopNav';
import InvoicePrintEmailActions from '../components/InvoicePrintEmailActions';
import { ShipDocInvoiceDocument } from './TransfersPage';
import './css/InvoicesPage.css';

const invoiceTaxSubtotal = (invoice) => {
  const explicitSubtotal = Number(invoice?.subtotalAmount ?? invoice?.subtotal);
  if (Number.isFinite(explicitSubtotal) && explicitSubtotal >= 0) return explicitSubtotal;

  const lineSubtotal = (invoice?.items || []).reduce((sum, item) => {
    const quantity = Math.abs(Number(item.quantity) || 0) || 1;
    const price = Number(item.price ?? item.costPer ?? item.actualPrice ?? 0) || 0;
    return sum + (quantity * price);
  }, 0);
  if (lineSubtotal > 0) return lineSubtotal;

  return Math.max(0, Number(invoice?.totalAmount || 0) - Number(invoice?.taxAmount || 0));
};

const taxAmountForRate = (invoice, rate) => Math.round(invoiceTaxSubtotal(invoice) * Number(rate || 0)) / 100;

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US');
};

function InvoicesPage({ embedded = false } = {}) {
  const initialQuery = new URLSearchParams(window.location.search).get('invoice')
    || new URLSearchParams(window.location.search).get('q')
    || '';
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [invoice, setInvoice] = useState(null);
  const [status, setStatus] = useState('');
  const [notice, setNotice] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [taxEditor, setTaxEditor] = useState({ open: false, value: '0', saving: false, error: '' });
  const [preferencesTaxRate, setPreferencesTaxRate] = useState(0);

  const loadInvoice = async (id) => {
    setStatus('');
    try {
      const res = await api.get(`/invoices/${id}`);
      setInvoice(res.data);
      setTaxEditor({ open: false, value: '', saving: false, error: '' });
    } catch (err) {
      console.error('Failed to load invoice:', err);
      setInvoice(null);
      setStatus('Invoice not found.');
    }
  };

  const searchInvoices = async (e) => {
    e?.preventDefault();
    const term = query.trim();
    if (!term) {
      setStatus('Enter an invoice number or customer name.');
      return;
    }

    setStatus('');
    try {
      const res = await api.get('/invoices/search', { params: { q: term, includeClosed: true } });
      const rows = res.data || [];
      setResults(rows);
      if (rows.length === 0) {
        setInvoice(null);
        setStatus('No invoices found.');
        return;
      }
      await loadInvoice(rows[0].id);
    } catch (err) {
      console.error('Failed to search invoices:', err);
      setStatus('Failed to search invoices.');
    }
  };

  useEffect(() => {
    document.title = "Invoices - Services Irene's Wigs.com";
    api.get('/preferences')
      .then((res) => setPreferencesTaxRate(Number(res.data?.taxRate || 0)))
      .catch((err) => console.error('Failed to load preferences:', err));
    if (initialQuery.trim()) {
      searchInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const printInvoice = () => {
    const source = document.querySelector('.invoice-page .shipdoc-invoice-workspace');
    if (!source || !invoice) return;

    const printNode = source.cloneNode(true);
    const iframe = document.createElement('iframe');
    iframe.title = 'Invoice print preview';
    iframe.style.position = 'fixed';
    iframe.style.left = '-10000px';
    iframe.style.top = '0';
    iframe.style.width = '850px';
    iframe.style.height = '1100px';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const cleanup = () => {
      setTimeout(() => iframe.remove(), 250);
    };

    const runPrint = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.addEventListener('afterprint', cleanup, { once: true });
      iframe.contentWindow.print();
      setTimeout(cleanup, 1000);
    };

    const doc = iframe.contentDocument || iframe.contentWindow.document;
    const documentStyles = Array.from(document.head.querySelectorAll('link[rel="stylesheet"], style'))
      .map((node) => node.outerHTML)
      .join('');
    doc.open();
    doc.write(`
      <!doctype html>
      <html>
        <head>
          <title>Invoice ${invoice.invoiceNo || ''}</title>
          ${documentStyles}
          <style>
            @page { size: portrait; margin: 10mm; }
            * { box-sizing: border-box; }
            body {
              background: #fff;
              color: #1f1f1f;
              font-family: Arial, Helvetica, sans-serif;
              margin: 0;
            }
            .invoice-sheet {
              background: #fff;
              box-shadow: none;
              color: #1f1f1f;
              font-family: Arial, Helvetica, sans-serif;
              margin: 0 auto;
              min-height: auto;
              padding: 44px 44px 64px;
              width: 100%;
            }
            .invoice-header {
              align-items: start;
              display: grid;
              gap: 30px;
              grid-template-columns: 1fr 1fr 280px;
              margin-bottom: 72px;
            }
            .invoice-header h1 {
              letter-spacing: 0;
              margin: 10px 0 0;
              font-size: 28px;
            }
            .invoice-brand {
              color: #5f6368;
              font-family: Georgia, serif;
              font-size: 34px;
              letter-spacing: 3px;
              line-height: 1;
              margin-top: 34px;
              text-align: center;
            }
            .invoice-brand span {
              display: block;
              font-family: Arial, Helvetica, sans-serif;
              font-size: 24px;
              font-weight: 700;
              letter-spacing: 0;
              margin-top: 4px;
            }
            .invoice-meta {
              border-collapse: collapse;
              font-size: 16px;
              text-align: center;
              width: 100%;
            }
            .invoice-meta th,
            .invoice-meta td {
              border: 1px solid #444;
              font-weight: 500;
              padding: 7px 9px;
            }
            .invoice-meta th {
              background: #efefef;
              text-align: center;
            }
            .invoice-parties {
              display: grid;
              gap: 120px;
              grid-template-columns: 1fr 1fr;
              margin-bottom: 12px;
            }
            .invoice-address {
              border: 1px solid #555;
              min-height: 158px;
            }
            .invoice-address-title {
              background: #f2f2f2;
              border-bottom: 1px solid #555;
              padding: 7px 9px;
            }
            .invoice-address-body {
              display: grid;
              gap: 5px;
              padding: 11px 12px 14px;
            }
            .invoice-city-line {
              display: grid;
              grid-template-columns: 1fr auto auto;
              gap: 34px;
            }
            .invoice-items {
              border-collapse: collapse;
              table-layout: fixed;
              width: 100%;
            }
            .invoice-items th {
              background: #efefef;
              border: 1px solid #555;
              font-size: 16px;
              font-weight: 500;
              padding: 7px 9px;
              text-align: center;
            }
            .invoice-items td {
              border: 0;
              font-size: 16px;
              padding: 10px 9px;
              vertical-align: top;
            }
            .invoice-items td:nth-child(3),
            .invoice-items td:nth-child(4) {
              text-align: right;
            }
            .invoice-items tfoot td {
              border-top: 2px solid #555;
              font-weight: 700;
              padding: 9px;
            }
            .invoice-items tfoot tr:last-child td {
              border-top: 2px solid #555;
              text-align: right;
            }
            .invoice-items th:first-child,
            .invoice-items td:first-child { width: 30%; }
            .invoice-items th:nth-child(2),
            .invoice-items td:nth-child(2) { width: 42%; }
            .invoice-items th:nth-child(3),
            .invoice-items td:nth-child(3) { width: 14%; }
            .invoice-items th:nth-child(4),
            .invoice-items td:nth-child(4) { width: 14%; }
            .transfer-invoice-memo {
              margin-top: 18px;
            }
            .shipdoc-invoice-workspace { background: #fff; padding: 0; }
            .shipdoc-invoice-sheet {
              border: 0;
              box-shadow: none;
              margin: 0;
              min-height: auto;
              padding: 50px 28px 40px;
              position: relative;
              width: 100%;
            }
            .shipdoc-paid-watermark { align-items: center; color: rgba(0, 0, 0, 0.72); display: flex; flex-direction: column; font-family: "Courier New", Courier, monospace; font-weight: 900; left: 50%; letter-spacing: 0; line-height: 0.92; pointer-events: none; position: absolute; text-align: center; text-transform: uppercase; top: 178px; transform: translateX(-50%) rotate(-18deg); z-index: 2; }
            .shipdoc-paid-watermark strong { border: 5px double currentColor; font-size: 44px; line-height: 0.9; padding: 3px 10px 0; }
            .shipdoc-paid-watermark span { font-size: 23px; margin-top: 2px; }
          </style>
        </head>
        <body>${printNode.outerHTML}</body>
      </html>
    `);
    doc.close();
    setTimeout(runPrint, 100);
  };

  const openTaxEditor = () => {
    if (!invoice?.editableTax) return;
    setTaxEditor({
      open: true,
      value: Number(invoice.taxAmount || 0) > 0 && preferencesTaxRate > 0 ? String(preferencesTaxRate) : '0',
      saving: false,
      error: '',
    });
  };

  const closeTaxEditor = () => {
    if (taxEditor.saving) return;
    setTaxEditor({ open: false, value: '0', saving: false, error: '' });
  };

  const saveInvoiceTax = async (event) => {
    event.preventDefault();
    if (!invoice?.invoiceNo) return;
    setTaxEditor((current) => ({ ...current, saving: true, error: '' }));
    setStatus('');
    try {
      const res = await api.patch(`/invoices/${invoice.invoiceNo}/tax`, {
        taxAmount: taxAmountForRate(invoice, taxEditor.value),
      });
      setInvoice(res.data);
      setTaxEditor({ open: false, value: '0', saving: false, error: '' });
      setStatus(`Invoice #${res.data?.invoiceNo || invoice.invoiceNo} tax updated.`);
    } catch (err) {
      setTaxEditor((current) => ({
        ...current,
        saving: false,
        error: err.response?.data?.error || err.message || 'Failed to update invoice tax.',
      }));
    }
  };

  return (
    <>
      {!embedded && <TopNav />}
      <main className={`invoice-page${embedded ? ' admin-dashboard-embedded-page admin-invoices-tab' : ''}`}>
        <div className="invoice-search">
          <form className="invoice-search-form" onSubmit={searchInvoices}>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Invoice # or customer name"
            />
            <button type="submit" className="button small blue">Find</button>
          </form>
          <InvoicePrintEmailActions
            invoice={invoice}
            loading={emailLoading}
            printButtonClassName="button small blue"
            emailButtonClassName="button small blue"
            onPrint={printInvoice}
            onLoadingChange={setEmailLoading}
            successMessage={(to) => `Invoice #${invoice.invoiceNo} emailed to ${to}.`}
            onError={(message) => {
              setStatus(message);
              setNotice(message);
            }}
          />
          <button
            type="button"
            className="button small yellow"
            onClick={openTaxEditor}
            disabled={!invoice?.editableTax}
          >
            Edit Tax
          </button>
        </div>

        {status && <p className="invoice-status">{status}</p>}

        {results.length > 1 && (
          <div className="invoice-results">
            {results.map((row) => (
              <button
                type="button"
                key={row.id}
                className={invoice?.id === row.id ? 'selected' : ''}
                onClick={() => loadInvoice(row.id)}
              >
                <strong>#{row.invoiceNo}</strong>
                <span>{formatDate(row.date)}</span>
                <span>{row.fromName}</span>
                {row.closed && <span className="invoice-result-badge">Closed</span>}
              </button>
            ))}
          </div>
        )}

        {invoice && (
          <section className="shipdoc-invoice-workspace">
            <ShipDocInvoiceDocument invoice={invoice} />
          </section>
        )}
        {notice && (
          <div className="invoice-alert-overlay">
            <div className="invoice-alert-modal" onClick={(e) => e.stopPropagation()}>
              <p>{notice}</p>
              <button type="button" className="button small red" onClick={() => setNotice('')} autoFocus>
                OK
              </button>
            </div>
          </div>
        )}
        {taxEditor.open && (
          <div className="invoice-alert-overlay" onClick={closeTaxEditor}>
            <form className="invoice-tax-modal" onSubmit={saveInvoiceTax} onClick={(e) => e.stopPropagation()}>
              <div className="invoice-tax-modal-title">
                <h3>Edit Invoice Tax</h3>
                <button type="button" onClick={closeTaxEditor} disabled={taxEditor.saving}>x</button>
              </div>
              <label>
                Tax
                <select
                  value={taxEditor.value}
                  onChange={(e) => setTaxEditor((current) => ({ ...current, value: e.target.value, error: '' }))}
                  autoFocus
                  required
                >
                  <option value="0">0%</option>
                  {preferencesTaxRate > 0 && (
                    <option value={String(preferencesTaxRate)}>{String(preferencesTaxRate)}%</option>
                  )}
                </select>
              </label>
              {taxEditor.error && <p className="invoice-tax-error">{taxEditor.error}</p>}
              <div className="invoice-tax-actions">
                <button type="button" className="button small gray" onClick={closeTaxEditor} disabled={taxEditor.saving}>
                  Cancel
                </button>
                <button type="submit" className="button small blue" disabled={taxEditor.saving}>
                  {taxEditor.saving ? 'Applying...' : 'Apply Tax'}
                </button>
              </div>
            </form>
          </div>
        )}
      </main>
    </>
  );
}

export default InvoicesPage;
